import { apiClient } from "@/lib/api-client";

/**
 * The Zen publisher map: parent publishers and their companies (subpublishers) by territory.
 * Backend: core/api/publisher_map_routes.py, data from the publisher map's tables (loaded from Zen's mapping sheet).
 *
 * - GET /api/admin/publisher-map (platform admins): every parent publisher.
 * - GET /api/org/publisher-map (seller, negotiator, admin): only the caller's own organization's group, picked
 *   on the server from the caller's profile. `linked` is false until that organization is given a group.
 */

/**
 * A register name Zen's mapping sheet groups under a contact route (a row of its Entity register), with its own
 * song count. It may be another company (Sony/ATV Tunes under Sony Music Publishing, United States) or the route's
 * own spelling (NAIVE 4CH under NAIVE): the data does not say which.
 */
export interface RouteMember {
  name: string;
  territory: string;
  linked_tracks: number;
  map_id: string;
}

export interface ParentPublisher {
  id: number;
  name: string;
  linked_tracks: number;
  /** Contact routes (the primary subpublisher per territory): the group's companies, as the page counts them. */
  subpublisher_count: number;
  territories: string[];
}

export interface Subpublisher {
  parent_id: number;
  name: string;
  territory: string;
  /** Other spellings the societies use for it. */
  spellings: string[];
  linked_tracks: number;
  /** Our tracks registered to it per society territory: US (MLC), UK (PRS), DE (GEMA), FR (SACEM). */
  registered: Record<"US" | "UK" | "DE" | "FR", number>;
  map_id: string;
  /** The register names grouped under this route, biggest first. */
  members: RouteMember[];
}

export interface PublisherMapResponse {
  as_of: string | null;
  source_note: string | null;
  has_data: boolean;
  /** Ranked by linked tracks, biggest first. */
  parents: ParentPublisher[];
  subpublishers: Subpublisher[];
}

export interface OrgPublisherMapResponse extends PublisherMapResponse {
  /** False when the caller's organization has no group on the map yet (the lists are then empty). */
  linked: boolean;
}

export async function fetchPublisherMap(signal?: AbortSignal): Promise<PublisherMapResponse> {
  return apiClient.get<PublisherMapResponse>("/api/admin/publisher-map", { signal });
}

export async function fetchOrgPublisherMap(signal?: AbortSignal): Promise<OrgPublisherMapResponse> {
  return apiClient.get<OrgPublisherMapResponse>("/api/org/publisher-map", { signal });
}
